import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { HERO_IMG, YARN_SPOOLS_IMG } from "../lib/spaces";
import { imgSrc } from "../lib/gallery";
import { scrollToId } from "../lib/scroll";

const LINES = [
  <>Your imagination.</>,
  <>Our yarn.</>,
  <>
    Your <span className="italic text-burgundy stitch-underline">one-of-a-kind</span> rug.
  </>,
];

export function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section ref={ref} className="relative overflow-hidden texture-weave" data-testid="hero">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 pt-10 pb-16 md:pt-16 md:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="flex items-center gap-2 text-xs sm:text-sm uppercase tracking-[0.22em] text-forest/70 font-medium"
          >
            <span className="inline-block h-2 w-2 rounded-full bg-burgundy" aria-hidden="true" />
            Custom tufted rugs · Made to order
          </motion.p>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-forest">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-charcoal/80"
          >
            Custom tufted rugs inspired by your favourite characters, games, words, and everything
            that makes your space yours.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.68 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <button
              onClick={() => scrollToId("customise")}
              data-testid="hero-design-my-rug-button"
              className="inline-flex items-center rounded-full bg-forest text-ivory font-medium px-7 py-3.5 transition-all duration-200 hover:bg-forest-deep hover:-translate-y-0.5 shadow-tuft"
            >
              Design My Rug
            </button>
            <button
              onClick={() => scrollToId("gallery")}
              data-testid="hero-explore-designs-button"
              className="inline-flex items-center rounded-full border-2 border-forest/25 text-forest font-medium px-7 py-3.5 transition-all duration-200 hover:border-forest hover:-translate-y-0.5"
            >
              Explore Designs
            </button>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-6 text-xs sm:text-sm text-charcoal/60"
          >
            Hand-punched yarn · One of one · Your idea, from sketch to soft
          </motion.p>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div style={{ y: yImg }} className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-3xl overflow-hidden border border-brass/40 shadow-tuft-lg"
            >
              <img
                src={HERO_IMG}
                alt="A tufting gun punching cream wool yarn into a stretched frame, a floral rug half finished"
                width={1264}
                height={848}
                className="w-full h-full object-cover aspect-[4/3]"
                fetchPriority="high"
              />
            </motion.div>
            <motion.img
              src={imgSrc(YARN_SPOOLS_IMG, 400)}
              alt="Colourful yarn cones and spools arranged for a rug project"
              width={200}
              height={200}
              loading="lazy"
              decoding="async"
              className="absolute -bottom-8 -left-4 sm:-left-8 w-28 sm:w-36 rounded-2xl border-4 border-ivory shadow-tuft-lg object-cover aspect-square"
            />
          </motion.div>
          <motion.button
            style={{ y: yCard }}
            onClick={() => scrollToId("tufting-experience")}
            data-testid="hero-watch-tufting-button"
            className="absolute top-4 -left-2 sm:top-6 sm:-left-6 inline-flex items-center gap-2 rounded-full bg-burgundy text-ivory text-xs sm:text-sm font-medium pl-3 pr-4 py-2.5 shadow-tuft-lg transition-transform duration-200 hover:-translate-y-0.5"
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-ivory/20">
              <ArrowDown className="h-3.5 w-3.5" />
            </span>
            Watch a rug being tufted
          </motion.button>
        </div>
      </div>
    </section>
  );
}
